import React, {useEffect, useRef} from 'react';
import {AccessibilityInfo, Animated, Pressable, StyleSheet, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {create} from 'zustand';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {controls, layout, radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Что произошло — сказанное там, где на это смотрят, и ненадолго.
 *
 * ## Зачем это, если есть `Banner`
 *
 * [`Banner`](./banner.tsx) — сообщение, которое ОСТАЁТСЯ: отвергнутый пароль рядом с
 * полем, предупреждение над разрушающей кнопкой. Оно живёт внутри формы и вместе с
 * ней. Ровно поэтому оно не годится для второй половины действий кабинета — тех, где
 * формы на экране больше нет: лист закрылся, строка исчезла, команда ушла на терминал.
 * Там до сих пор не говорилось НИЧЕГО, и «получилось» приходилось выводить из того,
 * что список перерисовался.
 *
 * Тост — ответ ровно на этот случай: короткая фраза поверх страницы, которая уходит
 * сама. Он не заменяет баннер и не должен: если сообщение нужно, пока человек
 * перечитывает набранное, оно обязано остаться на экране, а не растаять за четыре
 * секунды.
 *
 * ## Отмена — только настоящая
 *
 * У тоста есть место под одно действие, и почти всегда это «Отменить». Здесь принят
 * жёсткий принцип: **кнопка отмены появляется, только если у действия есть обратный
 * вызов, который можно сделать прямо сейчас** (вернуть прежний режим прохода, записать
 * прежний график). Распространённая альтернатива — отложить сам запрос на несколько
 * секунд и слать его, если не отменили, — здесь отвергнута сознательно: закрытая
 * вкладка в это окно означала бы, что отзыв сессии, который человек видел
 * подтверждённым, не случился. Для СКУД это не «мелкая неточность», а дыра.
 *
 * ## Как это вызывается
 *
 * Императивно и снаружи React — `toast.success(...)`, — потому что главный вызывающий
 * не компонент, а `MutationCache` в [`providers`](../../app/providers.tsx): мутация
 * объявляет `meta: {toast: 'ключ'}`, и рама говорит за неё. Экран, которому нужна
 * подстановка или отмена, зовёт `toast.*` сам из `onSuccess`.
 */
export type ToastTone = 'success' | 'error' | 'info';

export type ToastAction = {
  label: string;
  onPress: () => void;
};

export type Toast = {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
  action?: ToastAction;
};

/** Сколько тост держится на экране. С кнопкой — дольше: её надо успеть прочесть и нажать. */
const PLAIN_MS = 4000;
const ACTION_MS = 8000;
/** Больше трёх — это уже не сообщение, а лента. Старшие уходят первыми. */
const MAX_VISIBLE = 3;

type ToastStore = {
  queue: Toast[];
  show: (toast: Omit<Toast, 'id'> & {id?: string}) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

const timers = new Map<string, ReturnType<typeof setTimeout>>();

let seq = 0;
const nextId = () => `toast-${(seq += 1)}`;

export const useToastStore = create<ToastStore>((set, get) => ({
  queue: [],
  show: toast => {
    const id = toast.id ?? nextId();
    set(s => ({queue: [...s.queue.filter(t => t.id !== id), {...toast, id}].slice(-MAX_VISIBLE)}));

    const existing = timers.get(id);
    if (existing) {
      clearTimeout(existing);
    }
    timers.set(
      id,
      setTimeout(() => get().dismiss(id), toast.action ? ACTION_MS : PLAIN_MS),
    );
    return id;
  },
  dismiss: id => {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
    set(s => ({queue: s.queue.filter(t => t.id !== id)}));
  },
  clear: () => {
    for (const timer of timers.values()) {
      clearTimeout(timer);
    }
    timers.clear();
    set({queue: []});
  },
}));

type Options = {message?: string; action?: ToastAction};

function push(tone: ToastTone, title: string, options?: Options): string {
  const id = useToastStore.getState().show({tone, title, ...options});
  // Экранный диктор не смотрит на всплывшее — ему это надо СКАЗАТЬ. Иначе результат
  // действия для него просто не существует.
  AccessibilityInfo.announceForAccessibility(
    options?.message ? `${title}. ${options.message}` : title,
  );
  return id;
}

/**
 * Императивная ручка. Живёт вне React намеренно: её главный вызывающий — глобальный
 * `MutationCache`, у которого нет ни компонента, ни хуков.
 */
export const toast = {
  success: (title: string, options?: Options) => push('success', title, options),
  error: (title: string, options?: Options) => push('error', title, options),
  info: (title: string, options?: Options) => push('info', title, options),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

const styles = StyleSheet.create({
  /* `box-none`: полоса тянется во всю ширину, но перехватывает касания только там,
     где реально нарисован тост, — иначе она глушила бы низ каждого экрана. */
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    gap: spacing(2),
  },
  /* На мониторе — в правом верхнем углу, как у ui-kit: внизу их закрывают
     всплывающие панели и подсказки строк таблицы. */
  hostDesktop: {alignItems: 'flex-end', paddingRight: spacing(5)},
  /* Тост ui-kit: `surface-overlay`, тонкая рамка, плотная тень, радиус 3, значок на
     плашке цвета исхода, полоска времени внизу. */
  toast: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing(3),
    width: '100%',
    maxWidth: 380,
    minHeight: controls.lg,
    paddingVertical: spacing(3),
    paddingLeft: spacing(3),
    paddingRight: spacing(2),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  glyph: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.xs,
  },
  body: {flex: 1, gap: 2, paddingTop: 2},
  title: {fontSize: 12, lineHeight: 16},
  action: {
    minHeight: controls.sm,
    justifyContent: 'center',
    paddingHorizontal: spacing(2),
    borderRadius: radii.xs,
    borderWidth: 1,
  },
  close: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.xs,
  },
  timer: {position: 'absolute', left: 0, bottom: 0, height: 2, opacity: 0.5},
});

function ToastCard({
  item,
  closeLabel,
  align,
}: {
  item: Toast;
  closeLabel: string;
  /** Куда прижать карточку внутри полосы: по центру на телефоне, вправо на десктопе. */
  align: 'center' | 'flex-end';
}) {
  const p = useTheme();
  const dismiss = useToastStore(s => s.dismiss);

  /*
    Тот же пружинный въезд, что у кнопки и карточки: всё нажимаемое и всё появляющееся
    в приложении двигается по одной физике.

    Едет ТОЛЬКО положение, без проявления из прозрачности. Узел с `opacity: 0` выпадает
    из дерева доступности и из попадания пальцем — то есть первые триста миллисекунд
    тост был бы невидим диктору и не нажимался бы, а «Отменить» живёт ровно эти
    секунды. Всплывающее сообщение обязано быть нажимаемым с первого кадра.
  */
  const enter = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.spring(enter, {
      toValue: 1,
      useNativeDriver: true,
      speed: 18,
      bounciness: 4,
    }).start();
  }, [enter]);

  const skin =
    item.tone === 'success'
      ? {ink: p.signalInk, wash: p.signalSoft, glyph: 'check' as IconName}
      : item.tone === 'error'
      ? {ink: p.destructiveInk, wash: p.destructiveSoft, glyph: 'warning' as IconName}
      : // Сведение — синим, не акцентом: акцент красный, и справка, набранная им,
        // читалась бы как тревога.
        {ink: p.infoInk, wash: p.infoSoft, glyph: 'mail' as IconName};

  /* Полоска времени жизни: повторяет таймер стора, чтобы было видно, сколько тост
     ещё простоит, — и чтобы «Отменить» нажимали, пока есть что отменять. */
  const life = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    Animated.timing(life, {
      toValue: 0,
      duration: item.action ? ACTION_MS : PLAIN_MS,
      useNativeDriver: false,
    }).start();
  }, [life, item.action]);

  return (
    <Animated.View
      style={{
        width: '100%',
        alignItems: align,
        transform: [
          {translateY: enter.interpolate({inputRange: [0, 1], outputRange: [16, 0]})},
        ],
      }}>
      <View
        testID={`toast-${item.tone}`}
        accessibilityLiveRegion="polite"
        style={[
          styles.toast,
          shadows.pop,
          {backgroundColor: p.popover, borderColor: p.borderStrong},
        ]}>
        <View style={[styles.glyph, {backgroundColor: skin.wash}]}>
          <Icon name={skin.glyph} size={14} color={skin.ink} />
        </View>
        <View style={styles.body}>
          <AppText variant="subtitle" style={styles.title} numberOfLines={2}>
            {item.title}
          </AppText>
          {item.message ? (
            <AppText variant="caption" color={p.textSecondary} numberOfLines={3}>
              {item.message}
            </AppText>
          ) : null}
        </View>
        <Animated.View
          pointerEvents="none"
          style={[
            styles.timer,
            {
              backgroundColor: skin.ink,
              width: life.interpolate({inputRange: [0, 1], outputRange: ['0%', '100%']}),
            },
          ]}
        />

        {item.action ? (
          <Pressable
            testID="toast-action"
            accessibilityRole="button"
            accessibilityLabel={item.action.label}
            onPress={() => {
              item.action?.onPress();
              dismiss(item.id);
            }}
            style={state => [
              styles.action,
              {
                backgroundColor:
                  state.pressed || (state as {hovered?: boolean}).hovered
                    ? p.primarySofter
                    : 'transparent',
                borderColor:
                  state.pressed || (state as {hovered?: boolean}).hovered
                    ? p.primary
                    : p.borderStrong,
              },
            ]}>
            <AppText variant="label" color={p.foreground}>
              {item.action.label}
            </AppText>
          </Pressable>
        ) : (
          <Pressable
            testID="toast-close"
            accessibilityRole="button"
            accessibilityLabel={closeLabel}
            onPress={() => dismiss(item.id)}
            style={state => [
              styles.close,
              {
                backgroundColor:
                  state.pressed || (state as {hovered?: boolean}).hovered
                    ? p.accent
                    : 'transparent',
              },
            ]}>
            <Icon name="close" size={14} color={p.mutedForeground} />
          </Pressable>
        )}
      </View>
    </Animated.View>
  );
}

/**
 * Место, где тосты появляются. Один на всё приложение, над навигатором.
 *
 * Снизу, и ВЫШЕ панели вкладок: сообщение, накрывшее навигацию, отнимает у человека
 * выход с экрана ровно в тот момент, когда он захотел уйти.
 */
export function ToastHost({closeLabel}: {closeLabel: string}) {
  const insets = useSafeAreaInsets();
  const {isDesktop} = useBreakpoint();
  const queue = useToastStore(s => s.queue);

  if (queue.length === 0) {
    return null;
  }

  return (
    <View
      testID="toast-host"
      pointerEvents="box-none"
      style={[
        styles.host,
        isDesktop && styles.hostDesktop,
        isDesktop
          ? {top: insets.top + spacing(5)}
          : {
              bottom: insets.bottom + spacing(4) + layout.tabBarHeight,
              paddingHorizontal: layout.gutter,
            },
      ]}>
      {queue.map(item => (
        <ToastCard
          key={item.id}
          item={item}
          closeLabel={closeLabel}
          align={isDesktop ? 'flex-end' : 'center'}
        />
      ))}
    </View>
  );
}
